import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
} from '@atlaskit/spotlight';
import { FadeIn } from '@atlaskit/motion';
import { Text } from "@atlaskit/primitives/compiled";
import Image from '@atlaskit/image';

import { type ActiveContent, useAudioListenTrigger } from '@atlassian/ai-audio';

import AudioV3ChangeboardingIllustration from '../../assets/AudioV3ChangeboardingIllustration.svg';

const i18n = defineMessages({
	audioListenButtonSpotlightChangeboardingHeader: {
		id: 'audio.listen-button-spotlight.changeboarding-header',
		description: 'Header for introducing the audio playlist feature in changeboarding.',
		defaultMessage: 'Build your playlist',
	},
	audioListenButtonSpotlightChangeboardingBody: {
		id: 'audio.listen-button-spotlight.changeboarding-body',
		description: 'Description for audio playlist feature in changeboarding.',
		defaultMessage: 'Add content to a queue and listen in Confluence at your desk or on-the-go.',
	},
	audioListenButtonSpotlightChangeboardingShowMeButton: {
		id: 'audio.listen-button-spotlight.changeboarding.button.show-me',
		description: 'Text for button that allows users to open the audio player in changeboarding.',
		defaultMessage: 'Show me',
	},
});

const styles = cssMap({
	root: {
		display: 'flex',
		height: '100%',
	},
});

export type AudioListenButtonSpotlightProps = {
	children: React.ReactNode;
	activeContent: ActiveContent;
	onShowMeClick: () => void;
	onDismissClick: () => void;
};

export const AudioListenButtonSpotlight: FC<AudioListenButtonSpotlightProps> = ({
	onDismissClick,
	onShowMeClick,
	children,
	activeContent,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isVisible, setIsVisible] = useState<boolean>(false);
	const { buttonRef, isAudioSupported, handleClick } = useAudioListenTrigger({
		activeContent,
		source: 'changeboarding',
		isAutoOpenDisabled: false,
	});

	const handleDismissChangeboarding = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'audioV3ChangeboardingDismiss',
				source: 'audioV3Changeboarding',
			},
		}).fire();
		setIsVisible(false);
		onDismissClick();
	};

	const handleShowMe = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'audioV3ChangeboardingShowMe',
				source: 'audioV3Changeboarding',
			},
		}).fire();
		handleClick();
		setIsVisible(false);
		onShowMeClick();
	};

	useEffect(() => {
		if (!isAudioSupported) return;

		const timer = setTimeout(() => {
			setIsVisible(true);
		}, 3000);

		return () => clearTimeout(timer);
	}, [isAudioSupported]);

	useEffect(() => {
		if (isVisible) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'audioV3Changeboarding',
				},
			}).fire();
		}
	}, [isVisible, createAnalyticsEvent]);

	return isAudioSupported ? (
		<div css={styles.root}>
			<PopoverProvider>
				<PopoverTarget>{children}</PopoverTarget>
				<PopoverContent
					dismiss={handleDismissChangeboarding}
					shouldDismissOnClickOutside={false}
					isVisible={isVisible}
					placement="left-start"
					offset={[0, 6]}
					strategy="absolute"
				>
					<FadeIn entranceDirection="left">
						{(props) => (
							<div {...props}>
								<SpotlightCard testId="spotlight">
									<SpotlightHeader>
										<SpotlightHeadline>
											{formatMessage(i18n.audioListenButtonSpotlightChangeboardingHeader)}
										</SpotlightHeadline>
										<SpotlightControls>
											<SpotlightDismissControl onClick={handleDismissChangeboarding} />
										</SpotlightControls>
									</SpotlightHeader>
									<SpotlightMedia>
										<Image src={AudioV3ChangeboardingIllustration} alt="" />
									</SpotlightMedia>
									<SpotlightBody>
										<Text>{formatMessage(i18n.audioListenButtonSpotlightChangeboardingBody)}</Text>
									</SpotlightBody>
									<SpotlightFooter>
										<SpotlightActions>
											<SpotlightPrimaryAction ref={buttonRef} onClick={handleShowMe}>
												{formatMessage(i18n.audioListenButtonSpotlightChangeboardingShowMeButton)}
											</SpotlightPrimaryAction>
										</SpotlightActions>
									</SpotlightFooter>
								</SpotlightCard>
							</div>
						)}
					</FadeIn>
				</PopoverContent>
			</PopoverProvider>
		</div>
	) : null;
};
